import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  Platform,
  ScrollView,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Icon from '@expo/vector-icons/MaterialIcons';

interface UnauthorizedScreenProps {
  isDarkMode?: boolean;
  colors?: any;
  route?: any;
}

const UnauthorizedScreen: React.FC<UnauthorizedScreenProps> = () => {
  const navigation = useNavigation();

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.content}>
          {/* Decorative Header Background */}
          <View style={styles.headerDecoration}>
            <View style={[styles.decorCircle, styles.decorCircle1]} />
            <View style={[styles.decorCircle, styles.decorCircle2]} />
            <View style={styles.glowCircle} />
          </View>

          {/* Icon with modern styling */}
          <View style={styles.iconContainer}>
            <Icon name="lock" size={64} color="#FCA5A5" />
          </View>

          {/* Error Code */}
          <Text style={styles.errorCode}>403</Text>

          {/* Title */}
          <Text style={styles.title}>
            Access Denied
          </Text>

          {/* Message */}
          <Text style={styles.message}>
            You need to be logged in to access this area. Please sign in or create an account to continue.
          </Text>

          {/* Info Card */}
          <View style={styles.infoCard}>
            <Icon name="info-outline" size={22} color="#FB923C" style={styles.infoIcon} />
            <Text style={styles.infoText}>
              Sign in to unlock personalized job notifications, save bookmarks, and access premium features.
            </Text>
          </View>

          {/* Action Buttons */}
          <View style={styles.buttonContainer}>
            {/* Primary Login Button */}
            <TouchableOpacity
              style={styles.primaryButton}
              onPress={() => navigation.navigate('Login' as never)}
              activeOpacity={0.8}
            >
              <Icon name="login" size={22} color="#0F172A" style={styles.buttonIcon} />
              <Text style={styles.primaryButtonText}>
                Sign In
              </Text>
            </TouchableOpacity>

            {/* Secondary Sign Up Button */}
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={() => navigation.navigate('Signup' as never)}
              activeOpacity={0.8}
            >
              <Icon name="person-add" size={22} color="#FFFFFF" style={styles.buttonIcon} />
              <Text style={styles.secondaryButtonText}>
                Create Account
              </Text>
            </TouchableOpacity>

            {/* Home Button */}
            <TouchableOpacity
              style={styles.outlineButton}
              onPress={() => {
                if (Platform.OS === 'web' && typeof window !== 'undefined') {
                  window.history.replaceState({}, document.title, '/');
                }
                navigation.navigate('Home' as never);
              }}
              activeOpacity={0.7}
            >
              <Icon name="home" size={22} color="#F9FAFB" style={styles.buttonIcon} />
              <Text style={styles.outlineButtonText}>
                Go to Home
              </Text>
            </TouchableOpacity>
          </View>

          {/* Help Link */}
          <TouchableOpacity
            style={styles.helpLink}
            onPress={() => navigation.navigate('Settings' as never)}
            activeOpacity={0.7}
          >
            <Icon name="help-outline" size={20} color="#FB923C" />
            <Text style={styles.helpText}>
              Need help? Contact Support
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};


const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0A0C0F', // Deep dark background
  },
  scrollContent: {
    flexGrow: 1,
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 48,
    position: 'relative',
  },
  headerDecoration: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 200,
    overflow: 'hidden',
  },
  decorCircle: {
    position: 'absolute',
    borderRadius: 9999,
    opacity: 0.15,
  },
  decorCircle1: {
    width: 300,
    height: 300,
    top: -150,
    right: -100,
    backgroundColor: '#451A03', // Dark brown
  },
  decorCircle2: {
    width: 200,
    height: 200,
    top: -80,
    left: -50,
    backgroundColor: '#1C1917', // Warm dark brown
  },
  glowCircle: {
    position: 'absolute',
    width: 400,
    height: 400,
    borderRadius: 200,
    top: -200,
    left: '50%',
    marginLeft: -200,
    backgroundColor: '#FBBF24',
    opacity: 0.03,
  },
  iconContainer: {
    width: 140,
    height: 140,
    borderRadius: 70,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    marginTop: 40,
    backgroundColor: 'rgba(252, 165, 165, 0.15)',
    ...Platform.select({
      ios: {
        shadowColor: '#FCA5A5',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
      },
      android: { elevation: 10 },
      web: {
        shadowColor: '#FCA5A5',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
      }
    }),
  },
  errorCode: {
    fontSize: 64,
    fontWeight: '800',
    marginBottom: 12,
    letterSpacing: -2,
    color: '#FBBF24', // Bright golden yellow
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
    color: '#F9FAFB', // Bright white
  },
  message: {
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'center',
    marginBottom: 28,
    maxWidth: 400,
    paddingHorizontal: 16,
    color: '#B8BFC8', // Light gray
  },
  infoCard: {
    width: '100%',
    maxWidth: 450,
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 18,
    borderRadius: 14,
    borderLeftWidth: 4,
    borderLeftColor: '#FB923C',
    borderWidth: 1,
    borderColor: '#374151',
    marginBottom: 32,
    backgroundColor: '#1A1D24', // Elevated dark
    ...Platform.select({
      ios: {
        shadowColor: '#FB923C',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
      },
      android: { elevation: 4 },
      web: {
        shadowColor: '#FB923C',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
      }
    }),
  },
  infoIcon: {
    marginRight: 14,
    marginTop: 1,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 21,
    color: '#B8BFC8', // Light gray
  },
  buttonContainer: {
    width: '100%',
    maxWidth: 400,
    gap: 14,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 14,
    backgroundColor: '#FBBF24', // Bright golden yellow
    ...Platform.select({
      ios: {
        shadowColor: '#FBBF24',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.45,
        shadowRadius: 16,
      },
      android: { elevation: 8 },
      web: {
        shadowColor: '#FBBF24',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.45,
        shadowRadius: 16,
      }
    }),
  },
  buttonIcon: {
    marginRight: 10,
  },
  primaryButtonText: {
    fontSize: 17,
    fontWeight: '700',
    letterSpacing: 0.3,
    color: '#0F172A', // Dark text on bright button
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 14,
    backgroundColor: '#FB923C', // Bright orange
    ...Platform.select({
      ios: {
        shadowColor: '#FB923C',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.35,
        shadowRadius: 12,
      },
      android: { elevation: 6 },
      web: {
        shadowColor: '#FB923C',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.35,
        shadowRadius: 12,
      }
    }),
  },
  secondaryButtonText: {
    color: '#FFFFFF',
    fontSize: 17,
    fontWeight: '700',
    letterSpacing: 0.3,
  },
  outlineButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 15,
    paddingHorizontal: 24,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: 'rgba(251, 191, 36, 0.3)',
    backgroundColor: 'rgba(251, 191, 36, 0.08)',
  },
  outlineButtonText: {
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: 0.2,
    color: '#F9FAFB',
  },
  helpLink: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 28,
    gap: 8,
    paddingVertical: 8,
  },
  helpText: {
    fontSize: 15,
    fontWeight: '600',
    letterSpacing: 0.2,
    color: '#FB923C',
  },
});

export default UnauthorizedScreen;
